import { Icon } from '@iconify/react'
import {
  addCustomDocument,
  canAddCustomDocument,
  MAX_CUSTOM_DOCUMENTS,
  MAX_CUSTOM_DOCUMENT_TITLE_LENGTH,
  removeCustomDocument,
  updateCustomDocument,
} from '@/lib/raspadinha-campaigns'
import type { RaspadinhaCustomDocument } from '@/types/raspadinha-campaign'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import { RaspadinhaPdfUploadField } from './RaspadinhaPdfUploadField'
import styles from './RaspadinhaCustomDocumentsEditor.module.css'

type RaspadinhaCustomDocumentsEditorProps = {
  documents: RaspadinhaCustomDocument[]
  onDocumentsChange: (next: RaspadinhaCustomDocument[]) => void
  onDocumentPdfChange: (index: number, url: string, name: string, file?: File | null) => void
  disabled?: boolean
}

export function RaspadinhaCustomDocumentsEditor({
  documents,
  onDocumentsChange,
  onDocumentPdfChange,
  disabled = false,
}: RaspadinhaCustomDocumentsEditorProps) {
  const canAdd = canAddCustomDocument(documents)

  const handleAddDocument = () => {
    if (!canAdd) return
    onDocumentsChange(addCustomDocument(documents))
  }

  const handleRemoveDocument = (documentId: string) => {
    onDocumentsChange(removeCustomDocument(documents, documentId))
  }

  const handleUpdateDocument = (
    documentId: string,
    patch: Partial<Omit<RaspadinhaCustomDocument, 'id'>>
  ) => {
    onDocumentsChange(updateCustomDocument(documents, documentId, patch))
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h4 className={styles.title}>Documentos personalizados</h4>
          <p className={styles.hint}>
            Adicione até {MAX_CUSTOM_DOCUMENTS} documentos extras além do regulamento e do
            certificado. Informe um título e envie o PDF de cada um.
          </p>
        </div>
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAddDocument}
          disabled={!canAdd || disabled}
          title={
            canAdd
              ? 'Adicionar documento personalizado'
              : `Limite de ${MAX_CUSTOM_DOCUMENTS} documentos personalizados atingido`
          }
        >
          <Icon icon="mdi:plus" aria-hidden />
          Adicionar documento ({documents.length}/{MAX_CUSTOM_DOCUMENTS})
        </button>
      </div>

      {documents.length === 0 ? (
        <p className={styles.emptyState}>Nenhum documento personalizado cadastrado.</p>
      ) : (
        <ul className={styles.documentList}>
          {documents.map((document, index) => (
            <li key={document.id} className={styles.documentCard}>
              <div className={styles.documentCardHeader}>
                <span className={styles.documentIndex}>Documento {index + 1}</span>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemoveDocument(document.id)}
                  disabled={disabled}
                  aria-label={`Remover documento ${index + 1}`}
                >
                  <Icon icon="mdi:trash-can-outline" aria-hidden />
                  Remover
                </button>
              </div>

              <div className={styles.documentFields}>
                <div className={editStyles.editField}>
                  <label className={editStyles.editLabel} htmlFor={`custom-doc-title-${document.id}`}>
                    Título do documento
                  </label>
                  <input
                    id={`custom-doc-title-${document.id}`}
                    type="text"
                    className={editStyles.editInput}
                    maxLength={MAX_CUSTOM_DOCUMENT_TITLE_LENGTH}
                    placeholder="Ex: Termo de adesão"
                    value={document.title}
                    disabled={disabled}
                    onChange={(event) =>
                      handleUpdateDocument(document.id, { title: event.target.value })
                    }
                  />
                </div>

                <RaspadinhaPdfUploadField
                  label="Arquivo PDF"
                  hint="(Documento exibido com o título informado acima)"
                  pdfUrl={document.pdf_url}
                  pdfName={document.pdf_name}
                  disabled={disabled}
                  onPdfChange={(url, name, file) => {
                    handleUpdateDocument(document.id, {
                      pdf_url: url,
                      pdf_name: name,
                    })
                    onDocumentPdfChange(index, url, name, file)
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
